import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  ArrowRight, Star, CheckCircle, 
  Award, Users, MapPin, Clock, Upload, Palette, FileText, Package, Ruler, Hammer, ShoppingCart, Truck,
  PlayCircle, X
} from 'lucide-react';
import ConsumerNav, { KXLogo, ConsumerFooter } from '@/components/ConsumerNav';
import { ContactSection } from '@/pages/ContactPage';
import { useBusinessHours, formatBusinessHoursSummary } from '@/hooks/useBusinessHours';

const API = process.env.REACT_APP_BACKEND_URL;
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/fe513edf-015d-40a6-8c8d-7fa7cbc411d3/images/447674206e17559a8a294c4caa281e00360aa950e9d8b8c94e97a12852b46da5.png";

// Helper to resolve image URLs (local vs external)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/')) {
    return `${API}${url}`;
  }
  return url;
};

// ── Video Explainer Modal ──────────────────────────────────────────
const VideoModal = ({ type, onClose }) => {
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await fetch(`${API}/api/public/videos`);
        const data = await res.json();
        const entry = data.videos?.[type];
        if (entry?.url) {
          setVideoUrl(entry.url.startsWith('/') ? `${API}${entry.url}` : entry.url);
        }
      } catch (_) {}
      setLoading(false);
    };
    fetchVideos();
  }, [type]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const labels = {
    pro: { title: 'How Professional Installation Works', subtitle: 'See the full KX Pro Install journey from design to completion' },
    diy: { title: 'How the DIY Order Process Works', subtitle: 'Order materials, measure at home, and install on your schedule' },
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid={`video-modal-${type}`}
    >
      <div
        className="relative w-full max-w-3xl bg-[#111] rounded-2xl overflow-hidden shadow-2xl border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-white/10">
          <div>
            <h2 className="text-white font-bold text-lg">{labels[type].title}</h2>
            <p className="text-white/50 text-sm mt-0.5">{labels[type].subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white transition-colors ml-4 mt-0.5 shrink-0"
            data-testid={`video-modal-close-${type}`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Video Area */}
        <div className="aspect-video w-full bg-black relative">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-white/20 border-t-white/80 rounded-full animate-spin" />
            </div>
          ) : videoUrl ? (
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              autoPlay
              className="w-full h-full object-contain"
              data-testid={`video-player-${type}`}
            />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-8">
              <div className="w-16 h-16 rounded-full border-2 border-white/20 flex items-center justify-center">
                <PlayCircle className="h-8 w-8 text-white/30" />
              </div>
              <div>
                <p className="text-white/70 font-semibold text-lg">Video Coming Soon</p>
                <p className="text-white/35 text-sm mt-1">Our team is putting together an explainer video for this path. Check back soon!</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Default fallback data
const defaultTestimonials = [
  {
    name: "Sarah Mitchell",
    location: "Toronto, ON",
    text: "KX Kitchens transformed our dated kitchen into a stunning modern space. The visualizer tool helped us see exactly what we were getting before making any commitment.",
    rating: 5,
    image_url: "https://i.pravatar.cc/100?img=1"
  },
  {
    name: "Michael Chen",
    location: "Vancouver, BC", 
    text: "Professional from start to finish. The team's attention to detail and craftsmanship exceeded our expectations. Our kitchen is now the heart of our home.",
    rating: 5,
    image_url: "https://i.pravatar.cc/100?img=3"
  },
  {
    name: "Emma Thompson",
    location: "Calgary, AB",
    text: "The online design process was incredibly easy. Being able to visualize different options saved us so much time and helped us make confident decisions.",
    rating: 5,
    image_url: "https://i.pravatar.cc/100?img=5"
  }
];

const defaultTransformations = [
  { title: "Modern Minimalist", location: "Toronto, ON", image_url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80" },
  { title: "Traditional Elegance", location: "Vancouver, BC", image_url: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80" },
  { title: "Contemporary Open", location: "Montreal, QC", image_url: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80" },
];

const LandingPage = () => {
  const [testimonials, setTestimonials] = useState(defaultTestimonials);
  const [transformations, setTransformations] = useState(defaultTransformations);
  const [activeProcess, setActiveProcess] = useState('pro');
  const [videoModal, setVideoModal] = useState(null); // 'pro' | 'diy' | null
  const { hours } = useBusinessHours();
  const hoursSummary = formatBusinessHoursSummary(hours);

  // Scroll to hash anchor after page mount (e.g. /#path-selection from another page)
  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 200);
      }
    }
  }, []);

  // Fetch dynamic content from API
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [reviewsRes, transformationsRes] = await Promise.all([
          fetch(`${API}/api/public/reviews`),
          fetch(`${API}/api/public/transformations`)
        ]);
        
        if (reviewsRes.ok) {
          const reviewsData = await reviewsRes.json();
          if (reviewsData.length > 0) {
            setTestimonials(reviewsData);
          }
        }
        
        if (transformationsRes.ok) {
          const transformationsData = await transformationsRes.json();
          if (transformationsData.length > 0) {
            setTransformations(transformationsData);
          }
        }
      } catch (error) {
        // Silently fail and use default data
        // Using default content data
      }
    };
    
    fetchContent();
  }, []);

  const trustStats = [
    { number: "500+", label: "Kitchens Renovated", icon: Award },
    { number: "10+", label: "Years Experience", icon: Clock },
    { number: "5.0", label: "Customer Rating", icon: Star },
    { number: "50+", label: "Cities Served", icon: MapPin },
  ];

  const proSteps = [
    { step: 1, title: "Upload Photo", desc: "Share a photo of your current kitchen", icon: Upload },
    { step: 2, title: "Choose Design", desc: "Select cabinets, counters & finishes", icon: Palette },
    { step: 3, title: "Review Quote", desc: "Get instant pricing for your project", icon: FileText },
    { step: 4, title: "Order Samples", desc: "Feel the materials before committing", icon: Package },
    { step: 5, title: "Final Measure", desc: "Our team measures your space precisely", icon: Ruler },
    { step: 6, title: "Installation", desc: "Expert installation by certified pros", icon: Hammer },
  ];

  const diySteps = [
    { step: 1, title: "Upload Photo", desc: "Share a photo of your current kitchen", icon: Upload },
    { step: 2, title: "Choose Design", desc: "Select cabinets, doors & finishes", icon: Palette },
    { step: 3, title: "Enter Measurements", desc: "Wall-by-wall guided input", icon: Ruler },
    { step: 4, title: "Review Pricing", desc: "Get instant pricing in real time", icon: FileText },
    { step: 5, title: "Order Samples", desc: "Feel the materials before committing", icon: Package },
    { step: 6, title: "Place Order", desc: "Confirm your custom order online", icon: ShoppingCart },
    { step: 7, title: "Product Delivered", desc: "Materials shipped straight to your door", icon: Truck },
  ];

  return (
    <div className="min-h-screen bg-white">
      <ConsumerNav transparent />

      {/* Hero Section — full bleed background */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Modern North American Kitchen with Island"
            className="w-full h-full object-cover grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/20" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-24 lg:pt-40 lg:pb-32">
          <div className="max-w-2xl">
            <div className="hero-badge mb-8 animate-fade-up">
              <span className="hero-badge-dot"></span>
              Custom Kitchen Refacing
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-8 animate-fade-up delay-100" data-testid="hero-title">
              Kitchens
              <span className="block text-brass mt-2">Designed for</span>
              <span className="block">Modern Living</span>
            </h1>
            <p className="text-lg lg:text-xl text-white/80 mb-8 max-w-xl leading-relaxed animate-fade-up delay-200">
              <span className="block">REIMAGINE your space</span>
              <span className="block">DEFINE it with KX</span>
            </p>

            {/* Dual path CTAs — above the fold */}
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-up delay-300" data-testid="hero-dual-cta">
              <Link to="/reface/start" data-testid="hero-pro-install-btn">
                <Button className="btn-accent w-full sm:w-auto text-base px-7 py-5 h-auto">
                  Get Professional Installation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/reface/start?mode=diy" data-testid="hero-diy-btn">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto text-base px-7 py-5 h-auto border-white/50 text-white hover:bg-white/10 hover:text-white hover:border-white bg-transparent"
                >
                  Start DIY Order
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block">
          <div className="w-7 h-12 border-2 border-white/40 rounded-full flex items-start justify-center p-2.5">
            <div className="w-1.5 h-3 bg-white/60 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Two Path Blocks */}
      <section className="bg-[#0a0a0a]" id="path-selection" data-testid="path-selection">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-white/10">

            {/* ── Professional Installation ── */}
            <div className="px-0 lg:pr-12 py-10 lg:py-20 flex flex-col">
              {/* Thumbnail image */}
              <div className="relative rounded-xl overflow-hidden mb-6 sm:mb-8" style={{ aspectRatio: '16/7' }}>
                <img
                  src={HERO_IMAGE}
                  alt="Professional kitchen installation"
                  className="w-full h-full object-cover grayscale brightness-75"
                />
                <div className="absolute inset-0 bg-black/40" />
                <div className="absolute top-4 left-4">
                  <span className="bg-black/80 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold px-4 py-2 rounded-full tracking-wider">
                    Professional Installation
                  </span>
                </div>
              </div>

              {/* Description */}
              <h3 className="text-xl font-bold text-white mb-3">We handle everything — start to finish</h3>
              <p className="text-white/60 text-sm leading-relaxed mb-6">
                You design your space with our AI visualizer tool and our certified KX installers take care of measurements, material sourcing, and full installation. You sit back while we transform your kitchen.
              </p>
              <ul className="space-y-2.5 mb-8">
                {[
                  'Your design & a photo-realistic AI render',
                  'Instant pricing when you want it, in real time',
                  'Custom measurement by a certified installer',
                  'Premium cabinet doors, drawers & hardware',
                  'Full installation with clean-up included',
                  'Warranty backed craftsmanship',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-white/55 text-sm">
                    <CheckCircle className="h-4 w-4 text-brass mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto flex flex-wrap items-center gap-3">
                <Link to="/reface/start" data-testid="hero-cta-btn">
                  <Button className="btn-accent text-sm w-full sm:w-auto">
                    Start Your Transformation
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <button
                  onClick={() => setVideoModal('pro')}
                  className="flex items-center gap-1.5 text-white/55 hover:text-white text-sm transition-colors"
                  data-testid="pro-learn-more-btn"
                >
                  <PlayCircle className="h-4 w-4" />
                  Learn More
                </button>
              </div>
            </div>

            {/* ── DIY Cabinet Materials ── */}
            <div className="px-0 lg:pl-12 py-10 lg:py-20 flex flex-col">
              {/* Thumbnail image */}
              <div className="relative rounded-xl overflow-hidden mb-6 sm:mb-8" style={{ aspectRatio: '16/7' }}>
                <img
                  src="https://images.unsplash.com/photo-1654124803082-59bfd2aa4e14?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                  alt="DIY cabinet materials"
                  className="w-full h-full object-cover grayscale"
                />
                <div className="absolute inset-0 bg-black/45" />
                <div className="absolute top-4 left-4">
                  <span className="bg-black/80 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold px-4 py-2 rounded-full tracking-wider">
                    DIY — Do it Yourself
                  </span>
                </div>
              </div>

              {/* Description */}
              <h3 className="text-xl font-bold text-white mb-3">Order the materials, install on your schedule</h3>
              <p className="text-white/60 text-sm leading-relaxed mb-6">
                Get the exact same premium cabinet doors, drawer fronts, and trim our professional installers use — ordered online and delivered straight to your door, ready to install yourself.
              </p>
              <ul className="space-y-2.5 mb-8">
                {[
                  'Visualize your new look with our AI render tool',
                  'Instant pricing when you want it, in real time',
                  'Enter measurements using our guided order form',
                  'Full KX materials catalog at DIY pricing',
                  'Option to have a pro verify measurements before production',
                  'Upgrade to full professional installation anytime',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-white/55 text-sm">
                    <CheckCircle className="h-4 w-4 text-brass mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto flex flex-wrap items-center gap-3">
                <Link to="/reface/start?mode=diy" data-testid="diy-cta-btn">
                  <Button className="btn-accent text-sm w-full sm:w-auto">
                    Start DIY Order
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <button
                  onClick={() => setVideoModal('diy')}
                  className="flex items-center gap-1.5 text-white/55 hover:text-white text-sm transition-colors"
                  data-testid="diy-learn-more-btn"
                >
                  <PlayCircle className="h-4 w-4" />
                  Learn More
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Trust & Credibility Section */}
      <section className="py-20 lg:py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-12">
            {trustStats.map((stat, i) => (
              <div key={i} className="trust-badge animate-fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="trust-badge-icon">
                  <stat.icon className="h-6 w-6" />
                </div>
                <div className="trust-badge-number">{stat.number}</div>
                <div className="trust-badge-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12 lg:mb-16">
            <span className="text-brass font-semibold tracking-wider text-sm uppercase mb-4 block">Our Process</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-8">
              How It Works
            </h2>

            {/* Toggle */}
            <div className="inline-flex bg-gray-100 rounded-full p-1">
              <button
                data-testid="process-toggle-pro"
                onClick={() => setActiveProcess('pro')}
                className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 ${
                  activeProcess === 'pro'
                    ? 'bg-[#141414] text-white shadow-md'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                <span className="hidden sm:inline">Professional </span>Installation
              </button>
              <button
                data-testid="process-toggle-diy"
                onClick={() => setActiveProcess('diy')}
                className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 ${
                  activeProcess === 'diy'
                    ? 'bg-[#141414] text-white shadow-md'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                DIY<span className="hidden sm:inline"> — Do it Yourself</span>
              </button>
            </div>

            <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg mt-6 sm:mt-8">
              {activeProcess === 'pro'
                ? 'From inspiration to installation in six simple steps'
                : 'Order your custom materials and install on your own schedule'}
            </p>
          </div>
          
          <div
            key={activeProcess}
            className={`grid gap-4 sm:gap-6 lg:gap-8 animate-fade-up ${
              activeProcess === 'pro'
                ? 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-6'
                : 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-7'
            }`}
          >
            {(activeProcess === 'pro' ? proSteps : diySteps).map((step, i) => (
              <div
                key={step.step}
                className="process-step"
                style={{ animationDelay: `${i * 0.07}s` }}
              >
                <div className="process-step-number">{step.step}</div>
                <step.icon className="h-7 w-7 mx-auto mb-4 text-brass" />
                <h3 className="process-step-title">{step.title}</h3>
                <p className="process-step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-16 lg:mt-20 flex flex-col sm:flex-row gap-4 justify-center">
            {activeProcess === 'pro' ? (
              <>
                <Link to="/reface/start">
                  <Button className="btn-accent text-base">
                    Start Your Design Journey
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/how-it-works">
                  <Button className="btn-outline text-base">
                    Learn More About Our Process
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/reface/start?mode=diy">
                  <Button className="btn-accent text-base">
                    Start DIY Order
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/diy-how-it-works">
                  <Button className="btn-outline text-base" data-testid="diy-learn-more-btn">
                    Learn More About DIY
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Featured Work Preview */}
      <section id="our-work" className="py-24 lg:py-32 bg-charcoal">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 mb-14">
            <div>
              <span className="text-brass font-semibold tracking-wider text-sm uppercase mb-4 block">Our Work</span>
              <h2 className="text-3xl lg:text-5xl font-bold text-white">
                Kitchen Transformations
              </h2>
            </div>
            <Link to="/gallery">
              <Button className="btn-outline border-white/70 text-white hover:bg-white hover:text-charcoal">
                View All Projects
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {transformations.slice(0, 3).map((project, i) => (
              <Link 
                to="/gallery" 
                key={project.id || i} 
                className="gallery-card group cursor-pointer animate-fade-up"
                style={{ animationDelay: `${i * 0.15}s` }}
              >
                <div className="aspect-[4/3] overflow-hidden gallery-card-image">
                  <img 
                    src={resolveImageUrl(project.image_url)} 
                    alt={project.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700"
                  />
                </div>
                <div className="p-6 bg-white">
                  <h3 className="text-lg font-semibold text-foreground group-hover:text-brass transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-muted-foreground text-sm flex items-center gap-1.5 mt-2">
                    <MapPin className="h-3.5 w-3.5" /> {project.location}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 lg:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16 lg:mb-20">
            <span className="text-brass font-semibold tracking-wider text-sm uppercase mb-4 block">Testimonials</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-5">
              What Our Clients Say
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Trusted by homeowners across Canada
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial, i) => (
              <div 
                key={testimonial.id || i} 
                className="testimonial-card animate-fade-up"
                style={{ animationDelay: `${i * 0.15}s` }}
              >
                <div className="testimonial-stars">
                  {[...Array(testimonial.rating)].map((_, j) => (
                    <Star key={j} className="h-5 w-5 fill-brass text-brass" />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <img 
                    src={resolveImageUrl(testimonial.image_url || testimonial.image)} 
                    alt={testimonial.name}
                    loading="lazy"
                    className="testimonial-avatar"
                  />
                  <div>
                    <div className="testimonial-name">{testimonial.name}</div>
                    <div className="testimonial-location">{testimonial.location}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            Ready to Transform
            <span className="block text-brass mt-2">Your Kitchen?</span>
          </h2>
          <p className="text-muted-foreground text-lg mb-12 max-w-2xl mx-auto">
            Whether DIY or a professional installation, start with our free design visualization tool. See your dream kitchen come to life in minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#path-selection">
              <Button className="btn-accent text-base w-full sm:w-auto">
                Start Free Design
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/gallery">
              <Button className="btn-outline w-full sm:w-auto">
                Explore Our Work
              </Button>
            </Link>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6 lg:gap-10 mt-14 text-sm text-muted-foreground">
            <span className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-brass" /> Free to use
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-brass" /> No commitment
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-brass" /> Instant results
            </span>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 lg:py-28 px-6 lg:px-12 bg-white" id="contact">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="inline-block text-brass text-xs uppercase tracking-[0.2em] font-medium mb-4">
                Get In Touch
              </span>
              <h2 className="font-display text-4xl sm:text-5xl text-kx-charcoal mb-6">
                Let's Talk About<br />
                <span className="text-brass">Your Kitchen</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Ready to transform your kitchen? Our design experts are here to help. Fill out the form and we'll be in touch within one business day.
              </p>
              <div className="space-y-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-brass/10 flex items-center justify-center shrink-0">
                    <Clock className="h-3.5 w-3.5 text-brass" />
                  </div>
                  <span data-testid="landing-business-hours">{hoursSummary}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-brass/10 flex items-center justify-center shrink-0">
                    <MapPin className="h-3.5 w-3.5 text-brass" />
                  </div>
                  Serving the Greater Toronto Area & beyond
                </div>
              </div>
            </div>
            <div className="bg-cream rounded-2xl p-8 lg:p-10">
              <ContactSection compact />
            </div>
          </div>
        </div>
      </section>

      <ConsumerFooter />

      {/* Video Explainer Modals */}
      {videoModal && (
        <VideoModal type={videoModal} onClose={() => setVideoModal(null)} />
      )}
    </div>
  );
};

export default LandingPage;
